import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { EditableText, EditableImage, HrefPill, useEditMode } from '../../components';

const SocialIcon = ({ d }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d={d} /></svg>
);

const SOCIAL_PATHS = {
  Facebook: 'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.3.2 2.3.2v2.5h-1.3c-1.3 0-1.7.8-1.7 1.6V12h2.9l-.5 2.9h-2.4v7A10 10 0 0 0 22 12z',
  Instagram: 'M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.1-1.1.1-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1-.1 1.2-.1 1.6-.1 4.7s0 3.5.1 4.7c.1 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1.1-.1 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c-.1-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-2.1-.4-1.2-.1-1.6-.1-4.7-.1zm0 3.1a4.9 4.9 0 1 1 0 9.8 4.9 4.9 0 0 1 0-9.8zm0 1.8a3.1 3.1 0 1 0 0 6.2 3.1 3.1 0 0 0 0-6.2zm5.1-2.1a1.1 1.1 0 1 1 0 2.3 1.1 1.1 0 0 1 0-2.3z',
  LinkedIn: 'M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8.3 18.3H5.7V10h2.7v8.3zM7 8.8a1.5 1.5 0 1 1 0-3.1 1.5 1.5 0 0 1 0 3.1zm11.3 9.5h-2.7v-4c0-1 0-2.2-1.4-2.2s-1.6 1.1-1.6 2.1v4.2H10V10h2.5v1.1h.1c.4-.7 1.3-1.4 2.6-1.4 2.7 0 3.2 1.8 3.2 4.1v4.5z',
  YouTube: 'M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z'
};

function FooterBlock({ data, onChange }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { editMode } = useEditMode();

  if (!data) return null;
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const logo = data.logo || {};
  const news = data.newsletter || {};
  const links = data.link_columns || [];
  const reach = data.reach_us || {};
  const socials = data.socials || [];
  const updateLogo = (patch) => update({ logo: { ...logo, ...patch } });
  const updateNews = (patch) => update({ newsletter: { ...news, ...patch } });
  const updateReach = (patch) => update({ reach_us: { ...reach, ...patch } });
  const updateLinkCol = (i, patch) => {
    const next = links.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    update({ link_columns: next });
  };
  const updateLinkItem = (colIdx, itemIdx, patch) => {
    const cols = links.map((c, idx) => {
      if (idx !== colIdx) return c;
      const items = (c.items || []).map((it, j) => (j === itemIdx ? { ...it, ...patch } : it));
      return { ...c, items };
    });
    update({ link_columns: cols });
  };
  const updateSocial = (i, patch) => {
    const next = socials.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    update({ socials: next });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSent(true);
    setEmail('');
    setTimeout(() => setSent(false), 4000);
  };

  const copyright = (data.copyright || '').replace('{{year}}', String(new Date().getFullYear()));

  return (
    <footer className="relative bg-espresso text-cream/85 overflow-hidden mt-20">
      <div
        className="absolute inset-0 opacity-[0.10] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #E8D1B9 1px, transparent 1px), radial-gradient(circle at 80% 60%, #E8D1B9 1px, transparent 1px)', backgroundSize: '40px 40px' }}
      />
      <div className="absolute -top-1/2 -left-1/3 w-[700px] h-[700px] rounded-full bg-terracotta/15 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-1/3 -right-1/4 w-[600px] h-[600px] rounded-full bg-olive/10 blur-[140px] pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-terracotta/60 to-transparent" />

      <div className="relative container-page pt-20 pb-10 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <EditableImage
              src={logo.src}
              alt={logo.alt || 'Logo'}
              onChange={(src, alt) => updateLogo({ src, alt })}
              className="h-12 w-auto"
            />
            <div>
              <EditableText value={logo.primary_label} onChange={(t) => updateLogo({ primary_label: t })} as="div" className="font-display text-2xl font-bold text-cream" />
              <EditableText value={logo.secondary_label} onChange={(t) => updateLogo({ secondary_label: t })} as="div" className="text-[10px] uppercase tracking-[0.25em] text-cream/60" />
            </div>
          </div>
          <EditableText value={data.tagline} onChange={(t) => update({ tagline: t })} as="p" multiline className="mt-6 text-sm leading-relaxed text-cream/70 max-w-sm" />

          <form onSubmit={onSubmit} className="mt-8 max-w-sm">
            <EditableText value={news.label} onChange={(t) => updateNews({ label: t })} as="label" className="block text-xs uppercase tracking-[0.25em] text-cream/60 mb-2" />
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={news.placeholder}
                className="flex-1 rounded-full bg-cream/10 border border-cream/20 px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-terracotta"
              />
              <button type="submit" className="rounded-full bg-gradient-to-br from-terracotta to-terracotta-deep text-cream h-12 w-12 flex items-center justify-center hover:shadow-glow hover:-translate-y-0.5 transition-all" aria-label="Subscribe">
                <Send size={16} />
              </button>
            </div>
            {sent && <p className="mt-2 text-xs text-accent-soft"><EditableText value={news.success_message} onChange={(t) => updateNews({ success_message: t })} as="span" /></p>}
          </form>
        </div>

        {links.map((col, colIdx) => (
          <div key={col.heading || colIdx} className="lg:col-span-2">
            <EditableText value={col.heading} onChange={(t) => updateLinkCol(colIdx, { heading: t })} as="h4" className="text-cream font-semibold mb-4 text-sm uppercase tracking-[0.15em]" />
            <ul className="space-y-2.5 text-sm">
              {(col.items || []).map((item, itemIdx) => (
                <li key={item.href || itemIdx}>
                  <Link to={item.href} className="text-cream/70 hover:text-accent transition-colors"><EditableText value={item.label} onChange={(t) => updateLinkItem(colIdx, itemIdx, { label: t })} as="span" /></Link>
                  <HrefPill value={item.href} onChange={(h) => updateLinkItem(colIdx, itemIdx, { href: h })} />
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="lg:col-span-2">
          <EditableText value={reach.heading} onChange={(t) => updateReach({ heading: t })} as="h4" className="text-cream font-semibold mb-4 text-sm uppercase tracking-[0.15em]" />
          <ul className="space-y-3 text-sm text-cream/75">
            <li className="flex gap-2"><MapPin size={16} className="mt-0.5 text-accent shrink-0" /><EditableText value={reach.address} onChange={(t) => updateReach({ address: t })} as="span" multiline className="whitespace-pre-line" /></li>
            <li className="flex gap-2"><Phone size={16} className="mt-0.5 text-accent shrink-0" /><div className="flex-1"><a href={reach.phone_href}><EditableText value={reach.phone} onChange={(t) => updateReach({ phone: t })} as="span" /></a><HrefPill value={reach.phone_href} onChange={(h) => updateReach({ phone_href: h })} placeholder="tel: link" /></div></li>
            <li className="flex gap-2"><Mail size={16} className="mt-0.5 text-accent shrink-0" /><div className="flex-1"><a href={reach.email_href}><EditableText value={reach.email} onChange={(t) => updateReach({ email: t })} as="span" /></a><HrefPill value={reach.email_href} onChange={(h) => updateReach({ email_href: h })} placeholder="mailto: link" /></div></li>
          </ul>
          <div className="mt-5 flex gap-2 flex-wrap">
            {socials.map((s, i) => (
              <div key={s.label || i} className="flex flex-col">
                <a href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} className="h-10 w-10 rounded-full border border-cream/20 text-cream/80 flex items-center justify-center hover:bg-terracotta hover:text-cream hover:border-terracotta hover:scale-110 transition-all duration-300">
                  <SocialIcon d={SOCIAL_PATHS[s.label] || ''} />
                </a>
                {editMode && (
                  <input
                    type="text"
                    value={s.label || ''}
                    onChange={(e) => updateSocial(i, { label: e.target.value })}
                    className="mt-1 text-[10px] bg-yellow-50 border border-yellow-300 px-1 py-0.5 rounded w-20"
                    placeholder="label"
                  />
                )}
                <HrefPill value={s.href} onChange={(h) => updateSocial(i, { href: h })} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative border-t border-cream/10">
        <div className="container-page py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-cream/55">
          {editMode ? (
            <EditableText value={data.copyright} onChange={(t) => update({ copyright: t })} as="span" />
          ) : (
            <span>{copyright}</span>
          )}
          <EditableText value={data.tagline_right} onChange={(t) => update({ tagline_right: t })} as="span" />
        </div>
      </div>
    </footer>
  );
}

export default FooterBlock;
